<template>
  <div class="chat-container" :class="{ 'sidebar-open': isSidebarOpen }">
    <!-- Mobile toggle button (hamburger) -->
    <button
      type="button"
      class="chat-sidebar-toggle"
      @click="toggleSidebar"
      :aria-expanded="isSidebarOpen ? 'true' : 'false'"
      aria-controls="chat-sidebar"
      :aria-label="isSidebarOpen ? 'Close chat menu' : 'Open chat menu'"
    >
      <Menu v-if="!isSidebarOpen" :size="20" :stroke-width="2" aria-hidden="true" />
      <X v-else :size="20" :stroke-width="2" aria-hidden="true" />
    </button>

    <!-- Backdrop for mobile when sidebar is open -->
    <div
      v-if="isSidebarOpen"
      class="chat-sidebar-backdrop"
      @click="closeSidebar"
      aria-hidden="true"
    ></div>

    <!-- Left Sidebar - Placeholder for future chat menu options -->
    <aside
      id="chat-sidebar"
      class="chat-sidebar"
      :class="{ 'is-open': isSidebarOpen }"
      aria-label="Chat menu"
    >
      <div class="chat-sidebar-header">
        <button
          type="button"
          class="chat-sidebar-new-btn"
          @click="startNewChatSession"
          aria-label="Start a new chat session"
        >
          <Plus :size="18" :stroke-width="2" aria-hidden="true" />
          <span>New Chat Session</span>
        </button>
      </div>

      <!-- Past chat sessions -->
      <nav class="chat-sidebar-sessions" aria-label="Past chat sessions">
        <p class="chat-sidebar-sessions-title">Past chats</p>
        <p v-if="isLoadingSessions" class="chat-sidebar-sessions-empty">Loading…</p>
        <p v-else-if="sessions.length === 0" class="chat-sidebar-sessions-empty">
          No past chats yet.
        </p>
        <ul v-else class="chat-sidebar-sessions-list">
          <li
            v-for="s in sessions"
            :key="s.session_id"
            class="chat-sidebar-session-row"
            :class="{ 'is-active': s.session_id === currentSessionId }"
          >
            <button
              type="button"
              class="chat-sidebar-session-item"
              @click="selectSession(s.session_id)"
              :title="s.preview || 'Chat session'"
            >
              <span class="chat-sidebar-session-preview">{{ s.preview || 'New conversation' }}</span>
              <span class="chat-sidebar-session-meta">{{ formatSessionDate(s.last_activity_at) }}</span>
            </button>
            <button
              type="button"
              class="chat-sidebar-session-star"
              :class="{ 'is-starred': s.starred }"
              @click.stop="toggleStar(s)"
              :aria-pressed="s.starred ? 'true' : 'false'"
              :aria-label="s.starred ? 'Unstar this chat session' : 'Star this chat session'"
              :title="s.starred ? 'Unstar' : 'Star'"
            >
              <Star
                :size="16"
                :stroke-width="2"
                :fill="s.starred ? 'currentColor' : 'none'"
                aria-hidden="true"
              />
            </button>
          </li>
        </ul>
      </nav>
      <div class="chat-sidebar-footer">
        <router-link :to="ROUTES.WELCOME" class="chat-sidebar-link">
          <HelpCircle :size="18" :stroke-width="2" aria-hidden="true" />
          <span>Help</span>
        </router-link>

        <!-- Mobile-only: footer content moved here so the chat page doesn't
             show a bottom footer on small screens. -->
        <div class="chat-sidebar-mobile-footer">
          <div class="chat-sidebar-mobile-footer-section">
            <p class="chat-sidebar-mobile-footer-heading">Explore</p>
            <ul class="chat-sidebar-mobile-footer-list">
              <li>
                <router-link :to="ROUTES.ABOUT_US" class="chat-sidebar-link chat-sidebar-link--sm">
                  About Us
                </router-link>
              </li>
              <li>
                <a href="/how-it-works" class="chat-sidebar-link chat-sidebar-link--sm">How it works</a>
              </li>
              <li>
                <a href="/faq" class="chat-sidebar-link chat-sidebar-link--sm">FAQ</a>
              </li>
            </ul>
          </div>

          <div class="chat-sidebar-mobile-footer-section">
            <p class="chat-sidebar-mobile-footer-heading">For partners</p>
            <ul class="chat-sidebar-mobile-footer-list">
              <li>
                <router-link :to="ROUTES.BECOME_PARTNER" class="chat-sidebar-link chat-sidebar-link--sm">
                  Become a partner
                </router-link>
              </li>
              <li>
                <router-link :to="ROUTES.RESEARCHER_LOGIN" class="chat-sidebar-link chat-sidebar-link--sm">
                  Researcher Login
                </router-link>
              </li>
            </ul>
          </div>

          <nav class="chat-sidebar-mobile-footer-legal" aria-label="Legal">
            <router-link :to="ROUTES.PRIVACY_POLICY" class="chat-sidebar-link chat-sidebar-link--sm">
              Privacy
            </router-link>
            <router-link :to="ROUTES.TERMS_OF_USE" class="chat-sidebar-link chat-sidebar-link--sm">
              Terms
            </router-link>
          </nav>

          <p class="chat-sidebar-mobile-footer-copy">&copy; We Search Research Inc. 2026</p>
        </div>
      </div>
    </aside>

    <!-- Main chat area -->
    <div class="chat-main">
    <!-- Messages Area - Scrollable with centered content -->
    <div class="messages-wrapper" ref="messagesContainer" role="log" aria-live="polite" aria-label="Chat messages">
      <!-- Empty State -->
      <div v-if="messages.length === 0 && !isTyping" class="empty-state">
        <div class="empty-state-content">
          <MessageCircle class="empty-icon" :size="64" :stroke-width="1.5" />
          <h3>Hello, I am the We Search Research Search Assistant</h3>
          <p>Start your search by typing a message below.</p>
        </div>
      </div>

      <!-- Messages -->
      <div v-for="(message, index) in messages" :key="index" class="message-turn" :class="message.sender">
        <div class="message-content-wrapper">
          <div class="message-bubble" :class="message.sender">
            <div class="message-text" v-html="renderMarkdown(message.text)"></div>
          </div>
        </div>
      </div>

      <!-- Typing Indicator -->
      <div v-if="isTyping" class="message-turn bot">
        <div class="message-content-wrapper">
          <div class="message-bubble bot typing-bubble">
            <div class="typing-indicator" aria-label="AI is typing">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom anchor used for auto-scroll -->
      <div ref="bottomAnchor" class="messages-bottom-anchor" aria-hidden="true"></div>
    </div>

    <!-- Composer Area - Fixed at bottom -->
    <div class="composer-container">
      <div class="composer-wrapper">
        <!-- Starter question bubbles - only on a new/empty chat session -->
        <div
          v-if="messages.length === 0 && !isTyping"
          class="starter-bubbles"
          role="group"
          aria-label="Suggested questions to get started"
        >
          <button
            v-for="q in starterQuestions"
            :key="q.id"
            type="button"
            class="starter-bubble"
            @click="useStarterQuestion(q.prompt)"
            :aria-label="`Ask: ${q.prompt}`"
          >
            <component :is="q.icon" :size="18" :stroke-width="2" aria-hidden="true" />
            <span>{{ q.label }}</span>
          </button>
        </div>

        <div class="composer">
          <input
            ref="composerInput"
            v-model="userInput"
            @keyup.enter="sendMessage"
            placeholder="Tell me about the type of clinical trial you are looking for..."
            class="composer-input"
            :disabled="isTyping"
            aria-label="Chat input"
            type="text"
          />
          <button
            @click="sendMessage"
            class="composer-send-btn"
            :disabled="isTyping || !userInput.trim()"
            aria-label="Send message"
          >
            <svg v-if="!isTyping" width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/>
            </svg>
            <svg v-else class="spinner" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="50.265" stroke-dashoffset="12.566"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import api from '@/api'
import { marked } from 'marked'
import { API_ENDPOINTS, HTTP_STATUS, ROUTES, ERROR_MESSAGES } from '@/constants'
import { MessageCircle, HelpCircle, Plus, Menu, X, FlaskConical, Brain, Search, Star } from 'lucide-vue-next'

const MOBILE_BREAKPOINT = 768

export default {
  name: 'Chat',
  components: {
    MessageCircle,
    HelpCircle,
    Plus,
    Menu,
    X,
    Star
  },
  data() {
    return {
      messages: [],
      userInput: '',
      isTyping: false,
      isLoadingHistory: false,
      isLoadingSessions: false,
      currentSessionId: null,
      sessions: [],
      studyId: null,  // Track study ID across conversation
      patientSummaryId: null,  // Track summary ID across conversation
      isSidebarOpen: true,
      isMobileViewport: false,
      starterQuestions: [
        {
          id: 'clinical-trial',
          label: 'Find a clinical trial',
          prompt: "I'm looking for a clinical trial.",
          icon: FlaskConical
        },
        {
          id: 'memory-problems',
          label: "I'm worried about my memory",
          prompt: "I'm worried about my memory.",
          icon: Brain
        },
        {
          id: 'explore-studies',
          label: 'Explore available studies',
          prompt: "I'd like to explore available studies.",
          icon: Search
        }
      ],
      ROUTES
    }
  },

  mounted() {
    this.checkAuthentication()
    // Support deep-links from emails (e.g. /chat?session=<id>) that return the
    // user to a specific chat session; otherwise load the latest history.
    const deepLinkSession = this.$route.query.session
    if (deepLinkSession) {
      this.loadConversationHistory(deepLinkSession)
    } else {
      this.loadConversationHistory()
    }
    this.loadChatSessions()
    this.handleViewportResize()
    window.addEventListener('resize', this.handleViewportResize)
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handleViewportResize)
  },

  watch: {
    'messages.length'() {
      this.scrollToBottom()
    },
    isTyping() {
      this.scrollToBottom()
    }
  },

  methods: {
    scrollToBottom(smooth = true) {
      this.$nextTick(() => {
        const behavior = smooth ? 'smooth' : 'auto'
        const anchor = this.$refs.bottomAnchor
        const container = this.$refs.messagesContainer

        // Scroll only the inner messages container. We intentionally do NOT
        // call window.scrollTo here: the chat surface is a fixed-height pane
        // (.chat-container is capped to calc(100vh - navbar) with
        // overflow:hidden), so scrolling the window just fights the mobile
        // virtual keyboard and visibly yanks the page on every keystroke.
        if (container) {
          container.scrollTo({ top: container.scrollHeight, behavior })
        } else if (anchor && typeof anchor.scrollIntoView === 'function') {
          // Fallback only when the container ref isn't available yet.
          anchor.scrollIntoView({ behavior, block: 'end' })
        }
      })
    },

    renderMarkdown(text) {
      return marked(text)
    },
    
    cleanAiResponse(text) {
      // Strip tags from the message for display to user
      // This mirrors the cleaning logic in the backend
      if (!text) return text
      
      let cleaned = text
      
      // Strip summary tags
      cleaned = cleaned.replace(/<\/?summary>/gi, '')
      
      // Strip study tags (both the authoritative <study>...</study> and orphaned tags)
      cleaned = cleaned.replace(/<study>.*?<\/study>/gis, '')
      cleaned = cleaned.replace(/<\/?study>/gi, '')
      
      // Strip ineligible tags
      cleaned = cleaned.replace(/<ineligible\s*\/?>|<ineligible>\s*true\s*<\/ineligible>/gi, '')
      
      // Strip notify tags
      cleaned = cleaned.replace(/<notify\s*\/?>/gi, '')
      
      // Strip parenthetical explanations about tags
      cleaned = cleaned.replace(/\(\s*[^)]*\b(?:tag|signal)s?\b[^)]*(?:notify|backend|study\s+team)[^)]*\)/gi, '')
      cleaned = cleaned.replace(/(?:\*\*\*+|\[\s*notify\s*\/?\s*\])/gi, '')
      
      return cleaned.trim()
    },

    focusInput() {
      // On mobile we deliberately do NOT auto-focus the input. Re-focusing
      // after send re-opens the on-screen keyboard, which both prevents the
      // user from reading the reply and (on iOS Chrome especially) causes
      // the page to visibly jump as the keyboard animates back in.
      if (this.isMobileViewport) return
      this.$nextTick(() => {
        const el = this.$refs.composerInput
        if (el && typeof el.focus === 'function') {
          el.focus()
        }
      })
    },

    checkAuthentication() {
      if (!this.$store.getters['auth/isAuthenticated']) {
        this.$router.push(ROUTES.HOME)
        return
      }
    },

    useStarterQuestion(prompt) {
      // Pre-populate the composer with the starter prompt and submit it
      // immediately so the user goes straight into the conversation.
      this.userInput = prompt
      this.sendMessage()
    },

    startNewChatSession() {
      // Local-only reset: the backend mints a new session_id on the next send.
      this.messages = []
      this.studyId = null
      this.patientSummaryId = null
      this.currentSessionId = null
      this.focusInput()
      this.autoCloseSidebarOnMobile()
    },

    handleViewportResize() {
      const isMobile = typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT
      const wasMobile = this.isMobileViewport
      this.isMobileViewport = isMobile
      // Toggle defaults only when crossing the breakpoint (or on first mount,
      // where wasMobile starts as false): open on desktop, closed on mobile.
      if (isMobile !== wasMobile) {
        this.isSidebarOpen = !isMobile
      }
    },

    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen
    },

    closeSidebar() {
      this.isSidebarOpen = false
    },

    autoCloseSidebarOnMobile() {
      if (this.isMobileViewport) {
        this.isSidebarOpen = false
      }
    },

    formatSessionDate(value) {
      if (!value) return ''
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return ''
      const now = new Date()
      const sameDay =
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate()
      if (sameDay) {
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
      return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
    },

    async loadChatSessions() {
      if (!this.$store.getters['auth/isAuthenticated']) return
      this.isLoadingSessions = true
      try {
        const response = await api.get(API_ENDPOINTS.CHAT_SESSIONS)
        this.sessions = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
          this.$store.dispatch('auth/logout')
          this.$router.push(ROUTES.HOME)
          return
        }
        // eslint-disable-next-line no-console
        console.warn('Failed to load chat sessions:', error)
        this.sessions = []
      } finally {
        this.isLoadingSessions = false
      }
    },

    async toggleStar(session) {
      if (!session || !session.session_id) return
      if (!this.$store.getters['auth/isAuthenticated']) return

      const nextStarred = !session.starred
      // Optimistic update: flip the flag and re-pin starred sessions to the top
      // immediately so the UI feels responsive.
      session.starred = nextStarred
      this.reorderSessions()

      try {
        await api.patch(API_ENDPOINTS.CHAT_SESSION_STAR(session.session_id), {
          starred: nextStarred
        })
      } catch (error) {
        // Revert on failure.
        session.starred = !nextStarred
        this.reorderSessions()
        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
          this.$store.dispatch('auth/logout')
          this.$router.push(ROUTES.HOME)
          return
        }
        // eslint-disable-next-line no-console
        console.warn('Failed to update chat session star:', error)
      }
    },

    reorderSessions() {
      // Stable sort: starred first, preserving the existing relative order
      // (which is most-recent-activity first from the backend).
      this.sessions = [...this.sessions].sort(
        (a, b) => (a.starred === b.starred ? 0 : a.starred ? -1 : 1)
      )
    },

    async selectSession(sessionId) {
      if (!sessionId || sessionId === this.currentSessionId) return
      // Reset workflow context when switching sessions; it will be re-derived
      // from the next AI response.
      this.studyId = null
      this.patientSummaryId = null
      this.currentSessionId = sessionId
      this.autoCloseSidebarOnMobile()
      await this.loadConversationHistory(sessionId)
    },

    async loadConversationHistory(sessionId = null) {
      if (!this.$store.getters['auth/isAuthenticated']) return
      this.isLoadingHistory = true
      try {
        const params = sessionId ? { session_id: sessionId } : undefined
        const response = await api.get(API_ENDPOINTS.CONVERSATION_HISTORY, { params })
        const conversations = Array.isArray(response.data) ? response.data : []
        // Backend returns conversations in chronological order. Each row
        // contains a paired user message and AI response.
        const restored = []
        for (const conv of conversations) {
          if (conv.message) {
            restored.push({ text: conv.message, sender: 'user' })
          }
          if (conv.response) {
            restored.push({ text: conv.response, sender: 'bot' })
          }
        }
        this.messages = restored
        // Pin subsequent messages to the same session.
        if (conversations.length > 0) {
          const lastSessionId = conversations[conversations.length - 1].session_id
          this.currentSessionId = lastSessionId || null
        } else {
          this.currentSessionId = null
        }
        this.scrollToBottom(false)
      } catch (error) {
        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
          this.$store.dispatch('auth/logout')
          this.$router.push(ROUTES.HOME)
          return
        }
        // Non-fatal: just log and continue with an empty chat.
        // eslint-disable-next-line no-console
        console.warn('Failed to load conversation history:', error)
      } finally {
        this.isLoadingHistory = false
      }
    },

    async sendMessage() {
      if (this.userInput.trim() === '') return

      this.checkAuthentication()

      const messageToSend = this.userInput.trim()
      this.userInput = ''

      this.messages.push({ text: messageToSend, sender: 'user' })
      this.isTyping = true

      // Create a placeholder for the streaming bot message (not yet added to messages array)
      const botMessage = { text: '', sender: 'bot', isStreaming: true }
      this.scrollToBottom()

      try {
        const token = this.$store.getters['auth/authToken']

        if (!token) {
          throw new Error('No authentication token found. Please log in again.')
        }

        // Build request payload
        const payload = { message: messageToSend }

        // Include session_id so the backend continues the same chat session.
        // When null/omitted, the backend mints a fresh session UUID.
        if (this.currentSessionId) {
          payload.session_id = this.currentSessionId
        }

        // Include study_id if we have one from a previous response to maintain context
        if (this.studyId) {
          payload.study_id = this.studyId
        }

        // Include patient_summary_id if we have one from a previous response
        if (this.patientSummaryId) {
          payload.patient_summary_id = this.patientSummaryId
        }

        // Use the streaming endpoint with Fetch API for proper streaming support in browsers
        const response = await fetch(api.defaults.baseURL + API_ENDPOINTS.CHAT_STREAM, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : ''
          },
          body: JSON.stringify(payload)
        })

        // Check if the response is OK before proceeding
        if (!response.ok) {
          const errorText = await response.text()
          throw new Error(errorText || `HTTP error! status: ${response.status}`)
        }

        // Handle the streaming response
        let fullResponse = ''
        let workflowStatus = null
        let isDone = false
        let hasError = false
        let errorMessage = ''
        let buffer = '' // Buffer for incomplete lines

        // Create a reader for the stream
        const reader = response.body.getReader()
        const decoder = new TextDecoder()

        while (!isDone && !hasError) {
          const { done, value } = await reader.read()
          
          if (done) {
            isDone = true
            break
          }

          // Decode the chunk and process it
          const chunk = decoder.decode(value, { stream: true })
          
          // Add to buffer and process complete lines
          buffer += chunk
          const lines = buffer.split('\n\n')
          buffer = lines.pop() || '' // Keep the last incomplete line in buffer
          
          for (const line of lines) {
            if (!line.trim()) continue
            
            // Parse the data line (format: "data: {json}")
            if (line.startsWith('data: ')) {
              try {
                const jsonData = line.substring(6) // Remove "data: " prefix
                const parsed = JSON.parse(jsonData)
                
                if (parsed.chunk) {
                  // Accumulate chunks but don't display yet
                  fullResponse += parsed.chunk
                  // Don't push to messages yet - wait for full response
                } else if (parsed.done) {
                  // Final metadata
                  if (parsed.session_id) {
                    this.currentSessionId = parsed.session_id
                  }
                  if (parsed.study_id) {
                    this.studyId = parsed.study_id
                  }
                  if (parsed.workflow) {
                    workflowStatus = parsed.workflow
                    
                    // Extract study_id and patient_summary_id from workflow status
                    if (workflowStatus.study_id) {
                      this.studyId = workflowStatus.study_id
                    }
                    if (workflowStatus.patient_summary_id) {
                      this.patientSummaryId = workflowStatus.patient_summary_id
                    }
                  }
                  isDone = true
                  break
                } else if (parsed.error) {
                  // Error message
                  errorMessage = parsed.error
                  hasError = true
                  break
                }
              } catch (e) {
                // console.error('Error parsing stream chunk:', e)
                continue
              }
            }
          }
        }

        // Remove the streaming flag
        botMessage.isStreaming = false
        
        // Set the full cleaned response text
        if (hasError && errorMessage) {
          botMessage.text = errorMessage
        } else if (!fullResponse) {
          // If no response was received, use the error message
          botMessage.text = ERROR_MESSAGES.CHAT_ERROR
        } else {
          // Display the full cleaned response only after streaming is complete
          botMessage.text = this.cleanAiResponse(fullResponse)
        }
        
        // Push botMessage with the full text
        this.messages.push(botMessage)
        
        // Scroll to bottom after setting the full text
        this.$nextTick(() => this.scrollToBottom())

        // console.log('Streaming chat response complete:', { sessionId: this.currentSessionId, studyId: this.studyId })

      } catch (error) {
        // Clean up typing state on error
        this.isTyping = false
        
        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
          this.$store.dispatch('auth/logout')
          this.$router.push(ROUTES.HOME)
          return
        }

        const errorMsg = error.response?.data?.message || ERROR_MESSAGES.CHAT_ERROR
        this.messages.push({
          text: errorMsg,
          sender: 'bot'
        })
      } finally {
        this.isTyping = false
        this.scrollToBottom()
        this.focusInput()
        // Refresh sidebar so a brand-new session appears and ordering updates.
        this.loadChatSessions()
      }
    }
  }
}
</script>

<style scoped>
/* Container - Full viewport */
.chat-container {
  /* Fill the available main height so the messages list is the only
     scrolling area within the chat surface. The navbar above takes
     ~80px, plus an extra top offset so the first message / empty-state
     heading is always clearly visible below the sticky AppNavbar.

     Mobile keyboard handling: the viewport meta in public/index.html
     declares `interactive-widget=resizes-content`, so on Chrome Android
     and iOS 17+ Safari the layout viewport (and therefore `100dvh`)
     shrinks when the on-screen keyboard opens. That keeps the composer
     above the keyboard with no JS required. Older browsers fall back to
     the `100vh` line below and degrade gracefully. */
  --app-navbar-height: 80px;
  --chat-top-offset: 3.5rem;
  /* Fallback for browsers without dvh support. */
  height: calc(100vh - var(--app-navbar-height));
  /* Dynamic viewport unit shrinks with the on-screen keyboard when the
     viewport meta declares `interactive-widget=resizes-content` (see
     public/index.html). This is the modern, supported way to keep the
     composer above the keyboard on Chrome Android and iOS 17+ Safari. */
  height: calc(100dvh - var(--app-navbar-height));
  background-color: #fbf6e9;
  display: flex;
  flex-direction: row;
  position: relative;
  overflow: hidden;
  padding-top: var(--chat-top-offset);
  box-sizing: border-box;
}

/* Left sidebar - placeholder for future chat menu options */
.chat-sidebar {
  flex: 0 0 20%;
  max-width: 20%;
  background-color: #10372f; /* dark green - matches AppFooter */
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  transition: transform 0.25s ease;
}

/* Mobile sidebar toggle (hamburger) - hidden on desktop */
.chat-sidebar-toggle {
  display: none;
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  z-index: 30;
  width: 2.25rem;
  height: 2.25rem;
  align-items: center;
  justify-content: center;
  background-color: #10372f;
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  cursor: pointer;
  padding: 0;
  transition: background-color 0.15s ease;
}

.chat-sidebar-toggle:hover,
.chat-sidebar-toggle:focus-visible {
  background-color: #154a3e;
  outline: none;
}

/* Backdrop shown behind the sidebar on mobile */
.chat-sidebar-backdrop {
  display: none;
  position: absolute;
  inset: 0;
  background-color: rgba(15, 23, 42, 0.45);
  z-index: 15;
  animation: fadeIn 0.15s ease-out;
}

.chat-sidebar-footer {
  margin-top: auto;
  padding: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.chat-sidebar-header {
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.chat-sidebar-new-btn {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.625rem;
  width: 100%;
  padding: 0.625rem 0.75rem;
  background-color: rgba(255, 255, 255, 0.06);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  font-size: 0.9375rem;
  font-family: inherit;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.chat-sidebar-new-btn:hover,
.chat-sidebar-new-btn:focus-visible {
  background-color: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.24);
  outline: none;
}

/* Past chat sessions list */
.chat-sidebar-sessions {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0.75rem 0.5rem 0.5rem 0.5rem;
  min-height: 0;
}

.chat-sidebar-sessions-title {
  margin: 0 0 0.5rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
}

.chat-sidebar-sessions-empty {
  margin: 0.25rem 0.5rem;
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.45);
}

.chat-sidebar-sessions-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.chat-sidebar-session-row {
  display: flex;
  align-items: stretch;
  border-radius: 6px;
  transition: background-color 0.15s ease;
}

.chat-sidebar-session-row:hover,
.chat-sidebar-session-row:focus-within {
  background-color: rgba(255, 255, 255, 0.08);
}

.chat-sidebar-session-row.is-active {
  background-color: rgba(255, 255, 255, 0.14);
}

.chat-sidebar-session-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.125rem;
  flex: 1 1 auto;
  min-width: 0;
  padding: 0.5rem 0.625rem;
  background: transparent;
  color: #cfe1dc;
  border: none;
  border-radius: 6px;
  font-family: inherit;
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;
  transition: color 0.15s ease;
}

.chat-sidebar-session-row:hover .chat-sidebar-session-item,
.chat-sidebar-session-item:focus-visible {
  color: #ffffff;
  outline: none;
}

.chat-sidebar-session-row.is-active .chat-sidebar-session-item {
  color: #ffffff;
}

.chat-sidebar-session-star {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 2rem;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  /* Hidden until the row is hovered/focused, unless already starred. */
  opacity: 0;
  transition: color 0.15s ease, opacity 0.15s ease;
}

.chat-sidebar-session-row:hover .chat-sidebar-session-star,
.chat-sidebar-session-row:focus-within .chat-sidebar-session-star,
.chat-sidebar-session-star.is-starred {
  opacity: 1;
}

.chat-sidebar-session-star:hover,
.chat-sidebar-session-star:focus-visible {
  color: #ffd66b;
  outline: none;
}

.chat-sidebar-session-star.is-starred {
  color: #ffd66b;
}

.chat-sidebar-session-preview {
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-sidebar-session-meta {
  font-size: 0.6875rem;
  color: rgba(255, 255, 255, 0.45);
}

.chat-sidebar-link {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem 0.75rem;
  color: #cfe1dc;
  text-decoration: none;
  font-size: 0.9375rem;
  border-radius: 6px;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.chat-sidebar-link:hover,
.chat-sidebar-link:focus-visible {
  background-color: rgba(255, 255, 255, 0.08);
  color: #ffffff;
}

.chat-sidebar-link--sm {
  padding: 0.4rem 0.5rem;
  font-size: 0.8125rem;
}

/* Mobile-only footer content (hidden by default, shown on small screens) */
.chat-sidebar-mobile-footer {
  display: none;
}

.chat-sidebar-mobile-footer-section + .chat-sidebar-mobile-footer-section {
  margin-top: 0.75rem;
}

.chat-sidebar-mobile-footer-heading {
  margin: 0 0 0.25rem 0.5rem;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #5e9b92;
}

.chat-sidebar-mobile-footer-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.chat-sidebar-mobile-footer-legal {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.75rem;
}

.chat-sidebar-mobile-footer-copy {
  margin: 0.5rem 0.5rem 0;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
}

/* Main chat area (messages + composer) */
.chat-main {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: 100%;
  overflow: hidden;
}

/* Bottom anchor used by scrollIntoView to keep the latest message in view */
.messages-bottom-anchor {
  height: 1px;
  width: 100%;
}

/* Messages Wrapper - Scrollable area */
.messages-wrapper {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-behavior: smooth;
  padding: 1.5rem 0 2rem 0;
}

/* Smooth scrollbar */
.messages-wrapper::-webkit-scrollbar {
  width: 8px;
}

.messages-wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.messages-wrapper::-webkit-scrollbar-thumb {
  background: #cfe1dc;
  border-radius: 4px;
}

.messages-wrapper::-webkit-scrollbar-thumb:hover {
  background: #5b9d90;
}

/* Empty State */
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 2rem;
}

.empty-state-content {
  text-align: center;
  max-width: 400px;
  animation: fadeIn 0.5s ease-out;
}

.empty-icon {
  color: #5b9d90;
  margin: 0 auto 1rem auto;
  display: block;
  animation: bounce 2s ease-in-out infinite;
}

.empty-state-content h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.5rem;
}

.empty-state-content p {
  font-size: 1rem;
  color: #64748b;
  line-height: 1.6;
}

/* Message Turn - Full-width container */
.message-turn {
  width: 100%;
  padding: 1rem 1.5rem;
  animation: slideIn 0.3s ease-out;
}

.message-turn.user {
  background-color: transparent;
}

.message-turn.bot {
  background-color: transparent;
}

/* Message Content Wrapper - Centered with max-width */
.message-content-wrapper {
  max-width: 48rem;
  margin: 0 auto;
  display: flex;
}

.message-turn.user .message-content-wrapper {
  justify-content: flex-end;
}

.message-turn.bot .message-content-wrapper {
  justify-content: flex-start;
}

/* Message Bubble */
.message-bubble {
  max-width: 70%;
  padding: 0.75rem 1rem;
  border-radius: 1.125rem;
  line-height: 1.6;
  word-wrap: break-word;
  transition: all 0.2s ease;
}

.message-bubble.user {
  background-color: #0e3b2e;
  color: #ffffff;
  border-radius: 1.5rem;
  padding: 0.875rem 1.25rem;
  box-shadow: 0 1px 2px rgba(14, 59, 46, 0.18);
}

.message-bubble.bot {
  background-color: #eef2ee;
  color: #0f172a;
  max-width: 100%;
  padding: 0.875rem 1.125rem;
  border-radius: 1.125rem;
}

/* Message Text */
.message-text {
  font-size: 0.9375rem;
}

.message-bubble.user .message-text {
  color: #ffffff;
}

/* Markdown Styling for Bot Messages */
.message-bubble.bot .message-text :deep(p) {
  margin: 0 0 0.75rem 0;
  line-height: 1.6;
}

.message-bubble.bot .message-text :deep(p:last-child) {
  margin-bottom: 0;
}

.message-bubble.bot .message-text :deep(ul),
.message-bubble.bot .message-text :deep(ol) {
  margin: 0.75rem 0;
  padding-left: 1.5rem;
}

.message-bubble.bot .message-text :deep(li) {
  margin: 0.375rem 0;
  line-height: 1.6;
}

.message-bubble.bot .message-text :deep(strong) {
  font-weight: 600;
  color: #0f172a;
}

.message-bubble.bot .message-text :deep(em) {
  font-style: italic;
}

.message-bubble.bot .message-text :deep(code) {
  background-color: #f1f5f9;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  font-size: 0.875em;
  font-family: 'Courier New', monospace;
}

.message-bubble.bot .message-text :deep(pre) {
  background-color: #1e293b;
  color: #f1f5f9;
  padding: 1rem;
  border-radius: 0.5rem;
  overflow-x: auto;
  margin: 0.75rem 0;
}

.message-bubble.bot .message-text :deep(pre code) {
  background-color: transparent;
  padding: 0;
  color: inherit;
}

/* User Message Markdown */
.message-bubble.user .message-text :deep(p) {
  margin: 0 0 0.5rem 0;
}

.message-bubble.user .message-text :deep(p:last-child) {
  margin-bottom: 0;
}

/* Typing Bubble */
.typing-bubble {
  background-color: #eef2ee;
  border-radius: 1.125rem;
  padding: 0.875rem 1.25rem;
  max-width: fit-content;
}

/* Typing Indicator */
.typing-indicator {
  display: flex;
  gap: 0.375rem;
  align-items: center;
}

.typing-indicator span {
  display: inline-block;
  width: 8px;
  height: 8px;
  background-color: #5b9d90;
  border-radius: 50%;
  animation: typingDot 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(1) {
  animation-delay: 0s;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

/* Composer Container */
.composer-container {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, #fbf6e9 85%, rgba(251, 246, 233, 0));
  padding: 1.5rem 1rem max(2rem, env(safe-area-inset-bottom, 2rem)) 1rem;
  z-index: 10;
}

.composer-wrapper {
  max-width: 48rem;
  margin: 0 auto;
}

/* Starter question bubbles - shown above the composer on a new chat session */
.starter-bubbles {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.starter-bubble {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #ffffff;
  border: 1px solid #e2e8df;
  border-radius: 1.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  line-height: 1.25;
  color: #0e3b2e;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(14, 59, 46, 0.05);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease;
  font-family: var(--font-family-base, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif);
}

.starter-bubble:hover {
  border-color: #0e3b2e;
  box-shadow: 0 4px 8px -2px rgba(14, 59, 46, 0.15);
}

.starter-bubble:focus-visible {
  outline: 2px solid #0e3b2e;
  outline-offset: 2px;
}

.starter-bubble:active {
  transform: translateY(1px);
}

.starter-bubble svg {
  flex-shrink: 0;
  color: #0e3b2e;
}

@media (max-width: 768px) {
  .starter-bubble {
    font-size: 0.8125rem;
    padding: 0.4375rem 0.875rem;
  }

  /* Touch devices have no hover, so always reveal the star toggle. */
  .chat-sidebar-session-star {
    opacity: 1;
  }
}

/* Composer - Modern pill design */
.composer {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #ffffff;
  border: 1px solid #e2e8df;
  border-radius: 1.75rem;
  padding: 0.375rem 0.375rem 0.375rem 1.25rem;
  box-shadow: 0 4px 6px -1px rgba(14, 59, 46, 0.06), 0 2px 4px -1px rgba(14, 59, 46, 0.04);
  transition: all 0.2s ease;
}

.composer:focus-within {
  border-color: #0e3b2e;
  box-shadow: 0 4px 12px -1px rgba(14, 59, 46, 0.15), 0 2px 8px -1px rgba(14, 59, 46, 0.08);
}

/* Composer Input */
.composer-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 1rem;
  line-height: 1.5;
  color: #0f172a;
  padding: 0.625rem 0;
  font-family: var(--font-family-base, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif);
}

.composer-input::placeholder {
  color: #94a3b8;
}

.composer-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Send Button */
.composer-send-btn {
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  border: none;
  background-color: #0e3b2e;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
}

.composer-send-btn:hover:not(:disabled) {
  background-color: #134d3c;
  transform: scale(1.05);
}

.composer-send-btn:active:not(:disabled) {
  transform: scale(0.95);
}

.composer-send-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: scale(1);
}

.composer-send-btn svg {
  width: 1.25rem;
  height: 1.25rem;
}

/* Spinner Animation */
.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(1rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(0.5rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-0.5rem);
  }
}

@keyframes typingDot {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  30% {
    transform: translateY(-0.5rem);
    opacity: 1;
  }
}

/* Tablet Responsive */
@media (max-width: 768px) {
  .chat-sidebar-toggle {
    display: inline-flex;
  }

  .chat-container {
    /* Make room at the top for the toggle button */
    --chat-top-offset: 3.75rem;
  }

  .chat-sidebar {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 20;
    flex: 0 0 auto;
    width: min(80vw, 18rem);
    max-width: min(80vw, 18rem);
    transform: translateX(-100%);
    box-shadow: 2px 0 12px rgba(0, 0, 0, 0.25);
  }

  .chat-sidebar.is-open {
    transform: translateX(0);
  }

  .sidebar-open .chat-sidebar-backdrop {
    display: block;
  }

  .message-turn {
    padding: 0.875rem 1rem;
  }

  .message-bubble {
    max-width: 85%;
  }

  .composer-container {
    padding: 1rem 0.75rem 1.5rem 0.75rem;
  }

  .composer-input {
    font-size: 1rem; /* Prevent zoom on iOS */
  }

  .chat-sidebar-mobile-footer {
    display: block;
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
  }
}

/* Mobile Responsive */
@media (max-width: 480px) {
  .chat-container {
    height: 100%; /* Fill parent container */
    --chat-top-offset: 0.75rem;
  }

  .messages-wrapper {
    padding: 0.5rem 0 1.5rem 0;
  }

  .message-turn {
    padding: 0.75rem 0.75rem;
  }

  .message-bubble {
    max-width: 90%;
    font-size: 0.9375rem;
  }

  .message-bubble.user {
    padding: 0.75rem 1rem;
  }

  .empty-state-content h3 {
    font-size: 1.25rem;
  }

  .empty-state-content p {
    font-size: 0.9375rem;
  }

  .composer-container {
    padding: 0.75rem 0.5rem 1.25rem 0.5rem;
  }

  .composer {
    padding: 0.25rem 0.25rem 0.25rem 1rem;
  }

  .composer-input {
    font-size: 1rem; /* Prevent zoom on iOS */
    padding: 0.5rem 0;
  }

  .composer-send-btn {
    width: 2.25rem;
    height: 2.25rem;
    min-width: 2.25rem; /* Ensure touch target size */
    min-height: 2.25rem;
  }

  .typing-indicator span {
    width: 6px;
    height: 6px;
  }
}

/* Improve touch targets on mobile */
@media (hover: none) and (pointer: coarse) {
  .composer-send-btn {
    min-width: 2.75rem;
    min-height: 2.75rem;
  }
}
</style>
